import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../../hooks/useAuth";
import {
  getTransactionHistory,
  getWithdrawHistory,
} from "../../store/slices/user";

const PAGE_SIZE = 10;

const paginationControlsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 8,
  marginTop: 8,
};

const paginationButtonStyle = {
  background: "color-mix(in srgb, var(--primaryColor) 8%, white)",
  color: "var(--primaryColor)",
  border:
    "1.5px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
  borderRadius: 6,
  padding: "6px 12px",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
  transition: "all 0.3s ease",
};

const paginationTextStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "color-mix(in srgb, var(--primaryColor) 80%, black)",
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const num = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(num) ? num : null;
};

const fromMeta = (meta, keys) => {
  if (!meta || typeof meta !== "object") return null;
  for (const key of keys) {
    const parsed = toNumber(meta[key]);
    if (parsed !== null) {
      return parsed;
    }
  }
  if (meta.pagination && typeof meta.pagination === "object") {
    const nested = fromMeta(meta.pagination, keys);
    if (nested !== null) return nested;
  }
  if (meta.meta && typeof meta.meta === "object") {
    const nested = fromMeta(meta.meta, keys);
    if (nested !== null) return nested;
  }
  return null;
};

const extractTotalFromMeta = (meta) =>
  fromMeta(meta, [
    "total",
    "totalCount",
    "count",
    "totalRecords",
    "totalItems",
    "recordsTotal",
  ]);

const extractLimitFromMeta = (meta) =>
  fromMeta(meta, ["limit", "pageSize", "perPage", "page_length"]);

const tableHeaders = ["วันเวลา", "ช่องทาง", "จำนวน", "สถานะ"];
const headerCellStyle = {
  padding: "12px 8px",
  borderBottom: "2px solid var(--primaryColor)",
  background:
    "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 15%, white), color-mix(in srgb, var(--primaryColor) 8%, white))",
  fontWeight: 700,
  fontSize: 14,
  color: "var(--primaryColor)",
  textAlign: "left",
};
const emptyTableCells = ["ไม่มีรายการ", "-", "-", "-"];

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const modalStyle = {
  width: "95%",
  maxWidth: 900,
  background:
    "linear-gradient(to bottom, white, color-mix(in srgb, var(--primaryColor) 3%, white))",
  color: "#111827",
  borderRadius: 16,
  padding: 24,
  maxHeight: "90vh",
  overflowY: "auto",
  overscrollBehavior: "contain",
  border: "3px solid var(--primaryColor)",
  boxShadow:
    "0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px color-mix(in srgb, var(--primaryColor) 30%, transparent)",
};

const gridStyle = { display: "grid", gridTemplateColumns: "1fr", gap: 20 };
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#ffffff",
  color: "#111827",
  border:
    "1.5px solid color-mix(in srgb, var(--primaryColor) 25%, transparent)",
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

export default function DWHistory() {
  const [open, setOpen] = useState(false);
  const [depositPage, setDepositPage] = useState(1);
  const [withdrawPage, setWithdrawPage] = useState(1);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userId = user?.id || user?.userId || user?.user_id;
  const transactionHistory = useSelector(
    (state) => state.user?.transactionHistory || [],
  );
  const withdrawHistory = useSelector(
    (state) => state.user?.withdrawHistory || [],
  );
  const transactionMeta = useSelector(
    (state) => state.user?.transactionHistoryMeta || null,
  );
  const withdrawMeta = useSelector(
    (state) => state.user?.withdrawHistoryMeta || null,
  );

  useEffect(() => {
    if (open) {
      setDepositPage(1);
      setWithdrawPage(1);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !userId) return;
    dispatch(
      getTransactionHistory({
        id: userId,
        page: depositPage,
        limit: PAGE_SIZE,
        type: "deposit",
        order: "desc",
      }),
    );
  }, [open, userId, dispatch, depositPage]);

  useEffect(() => {
    if (!open || !userId) return;
    dispatch(
      getWithdrawHistory({
        id: userId,
        page: withdrawPage,
        limit: PAGE_SIZE,
        order: "desc",
      }),
    );
  }, [open, userId, dispatch, withdrawPage]);

  const formatDate = (value) => {
    if (!value) return "-";
    return moment(value).add(543, "year").format("DD-MM-YYYY > HH:mm");
  };

  const formatAmount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const mapStatus = (status, fallback) => {
    if (!status) return fallback || "-";
    const text = String(status).toUpperCase();
    switch (text) {
      case "SUCCESS":
      case "APPROVED":
      case "COMPLETE":
        return "สำเร็จ";
      case "PENDING":
      case "CHECKING":
      case "WAITING":
        return "รอดำเนินการ";
      case "REJECT":
      case "FAILED":
      case "CANCEL":
      case "CANCELLED":
        return "ไม่สำเร็จ";
      default:
        return status;
    }
  };

  const describeDepositChannel = (item) => {
    const channel =
      item?.channel || item?.platform || item?.provider || item?.method;
    if (channel) return channel;
    const type = String(item?.topupType || "").toUpperCase();
    if (type === "CASHTOCREDIT") return "แลกเงินสดเป็นเครดิต";
    if (type === "MANUALDEPOSIT") return "แมนนวล";
    if (type === "DEPOSIT") return "อัตโนมัติ";
    return item?.topupType || "-";
  };

  const describeWithdrawChannel = (item) => {
    return (
      item?.channel || item?.bankName || item?.provider || item?.method || "-"
    );
  };

  const rawDepositRows = useMemo(() => {
    if (!Array.isArray(transactionHistory)) return [];
    return [...transactionHistory]
      .filter((item) => {
        const type = String(item?.topupType || "").toUpperCase();
        return (
          type === "DEPOSIT" ||
          type === "MANUALDEPOSIT" ||
          type === "CASHDEPOSIT" ||
          type === "MANUALCASHDEPOSIT" ||
          type === "CASHTOCREDIT"
        );
      })
      .sort((a, b) => {
        const aTime = new Date(a?.created_at || a?.createdAt || 0).getTime();
        const bTime = new Date(b?.created_at || b?.createdAt || 0).getTime();
        return bTime - aTime;
      });
  }, [transactionHistory]);

  const rawWithdrawRows = useMemo(() => {
    if (!Array.isArray(withdrawHistory)) return [];
    return [...withdrawHistory].sort((a, b) => {
      const aTime = new Date(a?.created_at || a?.createdAt || 0).getTime();
      const bTime = new Date(b?.created_at || b?.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [withdrawHistory]);

  const depositTotal = extractTotalFromMeta(transactionMeta);
  const depositLimitFromMeta = extractLimitFromMeta(transactionMeta);
  const effectiveDepositLimit =
    depositLimitFromMeta && depositLimitFromMeta > 0
      ? depositLimitFromMeta
      : PAGE_SIZE;
  const depositTotalPages =
    depositTotal !== null && effectiveDepositLimit > 0
      ? Math.max(1, Math.ceil(depositTotal / effectiveDepositLimit))
      : null;
  const depositHasPrev = depositPage > 1;
  const depositHasNext =
    depositTotalPages !== null
      ? depositPage < depositTotalPages
      : rawDepositRows.length > effectiveDepositLimit;

  const withdrawTotal = extractTotalFromMeta(withdrawMeta);
  const withdrawLimitFromMeta = extractLimitFromMeta(withdrawMeta);
  const effectiveWithdrawLimit =
    withdrawLimitFromMeta && withdrawLimitFromMeta > 0
      ? withdrawLimitFromMeta
      : PAGE_SIZE;
  const withdrawTotalPages =
    withdrawTotal !== null && effectiveWithdrawLimit > 0
      ? Math.max(1, Math.ceil(withdrawTotal / effectiveWithdrawLimit))
      : null;
  const withdrawHasPrev = withdrawPage > 1;
  const withdrawHasNext =
    withdrawTotalPages !== null
      ? withdrawPage < withdrawTotalPages
      : rawWithdrawRows.length > effectiveWithdrawLimit;

  const depositRows = useMemo(() => {
    if (!rawDepositRows.length) return rawDepositRows;
    return rawDepositRows.slice(0, effectiveDepositLimit);
  }, [rawDepositRows, effectiveDepositLimit]);

  const withdrawRows = useMemo(() => {
    if (!rawWithdrawRows.length) return rawWithdrawRows;
    return rawWithdrawRows.slice(0, effectiveWithdrawLimit);
  }, [rawWithdrawRows, effectiveWithdrawLimit]);

  const handleDepositPrev = () => {
    if (!depositHasPrev) return;
    setDepositPage((prev) => Math.max(1, prev - 1));
  };

  const handleDepositNext = () => {
    if (!depositHasNext) return;
    setDepositPage((prev) => prev + 1);
  };

  const handleWithdrawPrev = () => {
    if (!withdrawHasPrev) return;
    setWithdrawPage((prev) => Math.max(1, prev - 1));
  };

  const handleWithdrawNext = () => {
    if (!withdrawHasNext) return;
    setWithdrawPage((prev) => prev + 1);
  };

  const renderDepositRow = (item) => {
    const key = item?.topupHistoryID || item?.id || item?.uuid;
    return (
      <tr
        key={key}
        style={{
          borderBottom:
            "1px solid color-mix(in srgb, var(--primaryColor) 12%, transparent)",
        }}
      >
        <td style={{ padding: "10px 8px", fontSize: 13 }}>
          {formatDate(item?.created_at || item?.createdAt)}
        </td>
        <td style={{ padding: "10px 8px", fontSize: 13, fontWeight: 600 }}>
          {describeDepositChannel(item)}
        </td>
        <td
          style={{
            padding: "10px 8px",
            textAlign: "right",
            fontSize: 13,
            fontWeight: 700,
            color: "var(--primaryColor)",
          }}
        >
          {formatAmount(item?.amount)}
        </td>
        <td style={{ padding: "10px 8px", fontSize: 13 }}>
          {mapStatus(item?.status, "สำเร็จ")}
        </td>
      </tr>
    );
  };

  const renderWithdrawRow = (item) => {
    const key = item?.withdrawRequestID || item?.id || item?.uuid;
    return (
      <tr
        key={key}
        style={{
          borderBottom:
            "1px solid color-mix(in srgb, var(--primaryColor) 12%, transparent)",
        }}
      >
        <td style={{ padding: "10px 8px", fontSize: 13 }}>
          {formatDate(item?.created_at || item?.createdAt)}
        </td>
        <td style={{ padding: "10px 8px", fontSize: 13, fontWeight: 600 }}>
          {describeWithdrawChannel(item)}
        </td>
        <td
          style={{
            padding: "10px 8px",
            textAlign: "right",
            fontSize: 13,
            fontWeight: 700,
            color: "var(--primaryColor)",
          }}
        >
          {formatAmount(item?.approveAmount || item?.amount)}
        </td>
        <td style={{ padding: "10px 8px", fontSize: 13 }}>
          {mapStatus(item?.status)}
        </td>
      </tr>
    );
  };

  const sections = [
    {
      title: "ประวัติการฝากเงิน",
      rows: depositRows,
      renderRow: renderDepositRow,
      pagination: {
        page: depositPage,
        totalPages: depositTotalPages,
        hasPrev: depositHasPrev,
        hasNext: depositHasNext,
        onPrev: handleDepositPrev,
        onNext: handleDepositNext,
      },
    },
    {
      title: "ประวัติการถอนเงิน",
      rows: withdrawRows,
      renderRow: renderWithdrawRow,
      pagination: {
        page: withdrawPage,
        totalPages: withdrawTotalPages,
        hasPrev: withdrawHasPrev,
        hasNext: withdrawHasNext,
        onPrev: handleWithdrawPrev,
        onNext: handleWithdrawNext,
      },
    },
  ];

  return (
    <div>
      <div
        style={{ cursor: "pointer", padding: 8 }}
        onClick={() => setOpen(true)}
      >
        <Icon
          icon="solar:clipboard-list-bold-duotone"
          width={36}
          height={36}
          color="var(--primaryColor)"
        />
        <div
          style={{
            color: "var(--primaryColor)",
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          ประวัติธุรกรรม
        </div>
      </div>

      {open && (
        <div style={overlayStyle} onClick={() => setOpen(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
                padding: "16px 20px",
                background:
                  "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 12%, white), color-mix(in srgb, var(--primaryColor) 6%, white))",
                borderRadius: 12,
                border:
                  "2px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "var(--primaryColor)",
                  fontSize: 20,
                  fontWeight: 800,
                }}
              >
                ประวัติการทำธุรกรรม
              </h3>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background:
                    "color-mix(in srgb, var(--primaryColor) 10%, transparent)",
                  color: "var(--primaryColor)",
                  border: "1.5px solid var(--primaryColor)",
                  borderRadius: 8,
                  padding: "6px 16px",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 14,
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--primaryColor)";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    "color-mix(in srgb, var(--primaryColor) 10%, transparent)";
                  e.currentTarget.style.color = "var(--primaryColor)";
                }}
              >
                ปิด
              </button>
            </div>

            <div style={gridStyle}>
              {sections.map((section) => (
                <div key={section.title}>
                  <h4
                    style={{
                      margin: "8px 0 12px 0",
                      color: "var(--primaryColor)",
                      fontSize: 17,
                      fontWeight: 800,
                      padding: "10px 16px",
                      background:
                        "linear-gradient(90deg, color-mix(in srgb, var(--primaryColor) 15%, white), transparent)",
                      borderLeft: "4px solid var(--primaryColor)",
                      borderRadius: 8,
                    }}
                  >
                    {section.title}
                  </h4>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        {tableHeaders.map((header) => (
                          <th key={header} style={headerCellStyle}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.length === 0 ? (
                        <tr>
                          {emptyTableCells.map((cell, index) => (
                            <td
                              key={index}
                              style={{
                                padding: "16px 8px",
                                textAlign: "center",
                                color:
                                  "color-mix(in srgb, var(--primaryColor) 60%, gray)",
                                fontStyle: "italic",
                              }}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ) : (
                        section.rows.map(section.renderRow)
                      )}
                    </tbody>
                  </table>
                  <div style={paginationControlsStyle}>
                    <button
                      type="button"
                      style={{
                        ...paginationButtonStyle,
                        opacity: section.pagination.hasPrev ? 1 : 0.4,
                        cursor: section.pagination.hasPrev
                          ? "pointer"
                          : "not-allowed",
                      }}
                      onClick={section.pagination.onPrev}
                      disabled={!section.pagination.hasPrev}
                    >
                      ก่อนหน้า
                    </button>
                    <span style={paginationTextStyle}>
                      หน้า {section.pagination.page}
                      {section.pagination.totalPages
                        ? ` / ${section.pagination.totalPages}`
                        : ""}
                    </span>
                    <button
                      type="button"
                      style={{
                        ...paginationButtonStyle,
                        opacity: section.pagination.hasNext ? 1 : 0.4,
                        cursor: section.pagination.hasNext
                          ? "pointer"
                          : "not-allowed",
                      }}
                      onClick={section.pagination.onNext}
                      disabled={!section.pagination.hasNext}
                    >
                      ถัดไป
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 24 }}>
              <button
                onClick={() => setOpen(false)}
                style={{
                  padding: "14px 10px",
                  width: "100%",
                  borderRadius: 12,
                  border: "2px solid var(--primaryColor)",
                  cursor: "pointer",
                  color: "#ffffff",
                  fontWeight: 800,
                  fontSize: 16,
                  background:
                    "linear-gradient(135deg, var(--primaryColor), color-mix(in srgb, var(--primaryColor) 85%, black))",
                  boxShadow:
                    "0 4px 15px color-mix(in srgb, var(--primaryColor) 40%, transparent)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px color-mix(in srgb, var(--primaryColor) 50%, transparent)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 15px color-mix(in srgb, var(--primaryColor) 40%, transparent)";
                }}
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
